import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Modal, Progress } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import {
  deleteOrderGenieRequest,
  getOrderGenieProcessStatus,
  getOrderGenieRequest,
  previewOrderGenieStoredFile,
  stopOrderGenieProcess,
} from '../../services/orderXApi';
import { STAGE_FALLBACK_LABELS } from '../../pages/orderXV4Utils';
import DocumentScanPreview from './DocumentScanPreview';

export const TERMINAL_STATUSES = new Set(['completed', 'matched', 'failed', 'cancelled']);
const POLL_INTERVAL_MS = 2500;
const TABULAR_CONTENT_TYPE_RE = /csv|excel|spreadsheet|ms-excel/i;

const formatElapsed = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m <= 0) return `${rem}s`;
  return `${m}m ${String(rem).padStart(2, '0')}s`;
};

// Polls a single request's process-status until it reaches a terminal state, showing
// real progress (stage/message/counts from the backend) instead of a fixed time
// estimate — actual duration depends on line-item count and document size, which
// varies far more than a "few seconds to a minute" claim can cover.
const ProcessingStatusCard = ({
  requestId,
  userId,
  requestName,
  onReady,
  onFailed,
  onLeaveInBackground,
  onCancelled,
}) => {
  const [status, setStatus] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [copied, setCopied] = useState(false);
  const [sourceFile, setSourceFile] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const startedAtRef = useRef(Date.now());
  const pollTimerRef = useRef(null);
  const cancelledRef = useRef(false);

  // Fetches the request's first source file + a rendered preview once, purely
  // to drive the "scanning" visual — the polling loop below owns actual status.
  useEffect(() => {
    let cancelled = false;
    getOrderGenieRequest(requestId, userId)
      .then((fullRequest) => {
        if (cancelled) return;
        const files = Array.isArray(fullRequest?.request?.source_files) ? fullRequest.request.source_files : [];
        const first = files[0] || null;
        setSourceFile(first);
        if (!first?.storage_path && !first?.filename) return;
        return previewOrderGenieStoredFile({
          storage_path: first.storage_path,
          filename: first.filename,
          content_type: first.content_type,
        });
      })
      .then((preview) => { if (!cancelled && preview) setSourcePreview(preview); })
      .catch(() => { /* no preview available — falls back to the file-icon placeholder */ });
    return () => { cancelled = true; };
  }, [requestId, userId]);

  useEffect(() => {
    cancelledRef.current = false;
    startedAtRef.current = Date.now();

    const tick = async () => {
      if (cancelledRef.current) return;
      try {
        const resp = await getOrderGenieProcessStatus(requestId, userId);
        const s = resp?.status || resp || {};
        const statusKey = String(s?.request_status || s?.status || '').toLowerCase();
        if (cancelledRef.current) return;
        setStatus(s);
        if (TERMINAL_STATUSES.has(statusKey)) {
          if (statusKey === 'failed' || statusKey === 'cancelled') {
            onFailed?.(s?.message || s?.detail || 'Processing failed.');
            return;
          }
          const fullRequest = await getOrderGenieRequest(requestId, userId);
          if (!cancelledRef.current) onReady?.(fullRequest);
          return;
        }
      } catch (err) {
        // transient poll failure — keep trying
      }
      if (!cancelledRef.current) {
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();

    const elapsedTimer = setInterval(() => {
      setElapsedSec((Date.now() - startedAtRef.current) / 1000);
    }, 1000);

    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      clearInterval(elapsedTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, userId]);

  const stageKey = String(status?.stage || status?.step || status?.status || 'queued').toLowerCase();
  const label = status?.message || STAGE_FALLBACK_LABELS[stageKey] || 'Working on your request…';
  const detail = status?.detail || '';

  const stageDone = Number(status?.stage_done) || 0;
  const stageTotal = Number(status?.stage_total) || 0;
  const percent = stageTotal > 0 ? Math.min(100, Math.round((stageDone / stageTotal) * 100)) : null;

  const lineItemsFound = stageTotal > 0 ? stageTotal : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(requestId));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) { /* clipboard unavailable — ignore */ }
  };

  const runCancelAndDelete = async () => {
    setCancelling(true);
    // Stop polling immediately so a stray in-flight response can't resurrect
    // status state (or fire onReady/onFailed) after the request is deleted.
    cancelledRef.current = true;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    try {
      await stopOrderGenieProcess(requestId, { user_id: userId }).catch(() => { /* best-effort — delete still proceeds */ });
      await deleteOrderGenieRequest(requestId, userId);
      onCancelled?.();
    } catch (err) {
      cancelledRef.current = false;
      setCancelling(false);
      onFailed?.(err?.message || 'Unable to cancel and delete this request.');
    }
  };

  const handleCancelAndDelete = () => {
    Modal.confirm({
      title: 'Cancel and delete this request?',
      content: `Request #${requestId} will stop processing and be permanently deleted. This can't be undone.`,
      okText: 'Cancel & Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep Processing',
      onOk: runCancelAndDelete,
    });
  };

  const tabular = TABULAR_CONTENT_TYPE_RE.test(String(sourceFile?.content_type || ''))
    || /\.(csv|xlsx?|xls)$/i.test(sourceFile?.filename || '');
  const hasImagePreview = Boolean(sourcePreview?.preview_image);

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-6">
        <Card className="shadow-sm border-0 h-100">
          <DocumentScanPreview
            previewUrl={hasImagePreview ? sourcePreview.preview_image : ''}
            previewType={hasImagePreview ? 'image/png' : ''}
            filename={sourceFile?.filename}
            scanning
            tabular={tabular}
            minHeight={360}
          />
        </Card>
      </div>

      <div className="col-12 col-lg-6">
        <Card className="shadow-sm border-0 py-3 h-100">
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center gap-2 mb-2">
              <span className="text-muted small">Request ID</span>
              <span className="badge bg-light text-dark border fs-6">#{requestId}</span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopy}
                title="Copy request ID"
              >
                {copied ? 'Copied' : ''}
              </Button>
            </div>
            {requestName && <div className="text-muted small">{requestName}</div>}
          </div>

          <div className="d-flex justify-content-center mb-3">
            {percent != null ? (
              <Progress type="circle" percent={percent} size={110} />
            ) : (
              <div className="spinner-border text-primary" role="status" style={{ width: 64, height: 64 }} />
            )}
          </div>

          <h5 className="text-center mb-1">{label}</h5>
          {detail && <p className="text-muted small text-center mb-1">{detail}</p>}
          {lineItemsFound != null && (
            <p className="text-muted small text-center mb-1">
              {stageDone} of {lineItemsFound} line item{lineItemsFound === 1 ? '' : 's'} processed
            </p>
          )}
          <p className="text-muted small text-center mb-4">Elapsed: {formatElapsed(elapsedSec)}</p>

          <div className="alert alert-light border small mb-3">
            <div className="mb-1">
              <strong>How long this takes:</strong> processing time depends on the number of
              line items and the size/complexity of your document — a short list can finish
              in under a minute, while a large multi-page order can take several minutes.
            </div>
            <div>
              This is running in the background on our server, not in your browser tab —
              it's safe to navigate away or close this page. Come back anytime and use
              Request <strong>#{requestId}</strong> to check on it or pick up review where you left off.
            </div>
          </div>

          <div className="text-center d-flex justify-content-center gap-2 flex-wrap">
            {onLeaveInBackground && (
              <Button onClick={onLeaveInBackground} disabled={cancelling}>I'll check back later</Button>
            )}
            <Button danger onClick={handleCancelAndDelete} loading={cancelling}>
              Cancel &amp; Delete Request
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ProcessingStatusCard;
